import {
  Alert,
  AlertDescription,
  Button,
  buttonVariants,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  useDebounceCallback,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  FEEDS_PAGE_LIMIT,
  useFeedsQuery,
  useSyncFeeds,
  useToggleFeedOrdering,
} from "@/modules/cf-feed/presentation/api/queries";
import type { FeedListItem } from "@/modules/cf-feed/presentation/api/types";
import { FeedsTable } from "@/modules/cf-feed/presentation/ui/FeedsTable";
import { FeedsToolbar } from "@/modules/cf-feed/presentation/ui/FeedsToolbar";
import { redirectToFacebookOAuth } from "@/modules/facebook/presentation/api/client";
import { useFacebookPagesQuery } from "@/modules/facebook/presentation/api/queries";
import { QueryState } from "@/shared/ui/QueryState";

export function FeedsManagePage() {
  const pagesQuery = useFacebookPagesQuery();
  const [offset, setOffset] = useState(0);
  const [limit, setLimit] = useState(FEEDS_PAGE_LIMIT);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<
    "active" | "draft" | "closed" | "inactive" | "all"
  >("all");
  const debouncedSearch = useDebounceCallback((value: string) => {
    setQ(value.trim());
    setOffset(0);
  }, 350);
  const feedsQuery = useFeedsQuery({
    feedType: "post",
    offset,
    limit,
    q: q || undefined,
    status: status === "all" ? undefined : status,
  });
  const syncFeeds = useSyncFeeds();
  const toggleOrdering = useToggleFeedOrdering();
  const [togglingId, setTogglingId] = useState<string | null>(null);

  const activePages =
    pagesQuery.data?.pages.filter((p) => p.status !== "disconnected") ?? [];
  const hasConnectedPage = activePages.length > 0;
  const feeds = feedsQuery.data?.feeds ?? [];
  const total = feedsQuery.data?.total ?? 0;

  const handleSync = () => {
    if (!hasConnectedPage) {
      toast.error("ກະລຸນາເຊື່ອມ Facebook Page ກ່ອນ");
      return;
    }
    syncFeeds.mutate(undefined);
  };

  const handleConnectFacebook = async () => {
    try {
      await redirectToFacebookOAuth("/app/feeds");
    } catch (e) {
      toast.error("ເລີ່ມ OAuth ລົ້ມເຫຼວ", {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  };

  const handleToggle = async (feed: FeedListItem, enabled: boolean) => {
    setTogglingId(feed.id);
    try {
      await toggleOrdering.mutateAsync({ id: feed.id, enabled });
    } catch {
      return;
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <>
      <Header />

      <Main>
        <FeedsToolbar onSync={handleSync} syncing={syncFeeds.isPending} />

        <div className="mb-4 flex flex-col gap-3 rounded-xl border bg-card p-4 sm:flex-row sm:items-end">
          <div className="min-w-0 flex-1 space-y-1">
            <label
              htmlFor="feed-search"
              className="text-muted-foreground text-xs"
            >
              ຄົ້ນຫາໂພສ
            </label>
            <Input
              id="feed-search"
              value={searchInput}
              placeholder="ຊື່ໂພສ, ຂໍ້ຄວາມ, Page ຫຼື Feed ID"
              onChange={(event) => {
                setSearchInput(event.target.value);
                debouncedSearch(event.target.value);
              }}
            />
          </div>
          <div className="w-full space-y-1 sm:w-48">
            <span className="text-muted-foreground text-xs">ສະຖານະ</span>
            <Select
              value={status}
              onValueChange={(value) => {
                setStatus(value as typeof status);
                setOffset(0);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="ທັງໝົດ" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">ທັງໝົດ</SelectItem>
                <SelectItem value="active">ໃຊ້ງານຢູ່</SelectItem>
                <SelectItem value="draft">ຮ່າງ</SelectItem>
                <SelectItem value="closed">ປິດຊົ່ວຄາວ</SelectItem>
                <SelectItem value="inactive">ບໍ່ໃຊ້ງານ</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {!hasConnectedPage && !pagesQuery.isLoading ? (
          <Alert className="mb-4">
            <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <span>ຍັງບໍ່ໄດ້ເຊື່ອມ Facebook Page — ຕ້ອງເຊື່ອມກ່ອນຈຶ່ງດຶງໂພສໄດ້</span>
              <Button type="button" size="sm" onClick={handleConnectFacebook}>
                ເຊື່ອມຕໍ່ກັບ Facebook
              </Button>
            </AlertDescription>
          </Alert>
        ) : null}

        {hasConnectedPage && activePages.some((p) => !p.webhookSubscribed) ? (
          <Alert className="mb-4" variant="destructive">
            <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span>
                ບາງ Page ຍັງບໍ່ໄດ້ subscribe webhook — ເປີດ toggle
                ຈະພະຍາຍາມເຊື່ອມໃຫ້ອັດຕະໂນມັດ
              </span>
              <Link
                to="/app/facebook"
                search={{
                  facebook: undefined,
                  reason: undefined,
                  pages: undefined,
                  missing: undefined,
                  omitted: undefined,
                }}
                className={buttonVariants({ variant: "outline", size: "sm" })}
              >
                ໄປຕັ້ງຄ່າ Page
              </Link>
            </AlertDescription>
          </Alert>
        ) : null}

        <QueryState
          result={feedsQuery}
          isEmpty={!feedsQuery.isLoading && total === 0}
          title="ຍັງບໍ່ມີໂພສ"
          description="ກົດ «ດຶງໂພສໃໝ່» ຫຼັງເຊື່ອມ Facebook Page"
        >
          <FeedsTable
            data={feeds}
            isLoading={feedsQuery.isFetching}
            togglingId={togglingId}
            offset={offset}
            limit={limit}
            total={total}
            onPaginationChange={(nextOffset, nextLimit) => {
              setOffset(nextOffset);
              setLimit(nextLimit);
            }}
            onToggle={handleToggle}
          />
        </QueryState>
      </Main>
    </>
  );
}
